import React from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { ServicesContext } from '~/services';
import { enumValue, gapIsPending } from '~/util';
import { BSModal } from '~/components/Bootstrap';
import { useRoleActions } from '~/util/useRoleActions';
import { FocSubmitRow } from '.';

const propTypes = {
    // eslint-disable-next-line react/require-default-props
    patient: PropTypes.shape({
        id: PropTypes.number,
        foc_gaps: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                submission_answer: PropTypes.shape({}),
                status: PropTypes.number,
            })
        ),
    }),
};

export function FocSubmit({ patient = {} }) {
    const context = React.useContext(ServicesContext);
    const roleCanSubmit = useRoleActions('allow_create_esubmission_foc_gap');
    const [show, setShow] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    async function submit() {
        setLoading(true);
        try {
            await context.patient.submitSubmission({
                patient: {
                    id: patient.id,
                },
                gap_type: enumValue(['gap', 'type', 'FOC Gap']),
            });
        } finally {
            setLoading(false);
            setShow(false);
            context.patient.load(patient.id);
        }
    }

    const canSubmit = patient.foc_gaps.some((gap) => gap.submission_answer && gapIsPending(gap));

    const submissionCategories = [
        {
            name: 'Primary',
            gaps: [],
        },
        {
            name: 'Secondary',
            gaps: [],
        },
    ];
    const categories = {
        'FOC Measures': 0,
        'FOC Measures/Secondary': 1,
    };

    patient.foc_gaps.forEach((gap) => {
        if (gapIsPending(gap) && gap.submission_answer) {
            const measureCategory =
                gap.measure.measure_category.description || 'No Measure Category';
            // Handle gap categories we haven't seen
            if (typeof categories[measureCategory] === 'undefined') {
                categories[measureCategory] = submissionCategories.length;
                submissionCategories.push({
                    name: measureCategory,
                    gaps: [gap],
                });
            } else {
                submissionCategories[categories[measureCategory]].gaps.push(gap);
            }
        }
    });

    // Sort gaps
    submissionCategories.forEach((cat) => {
        cat.gaps.sort((gap) => {
            return gap.measure?.sequence || 0;
        });
    });

    function displayTableBody(cat) {
        return cat.gaps.map((gap) => (
            <React.Fragment key={gap.id}>
                <tr>
                    <td colSpan={4}>
                        <span className="fw-bold ms-2">{gap.measure.focs.map((x) => x?.type)}</span>
                    </td>
                </tr>
                {gap.measure.focs.map((x) =>
                    x.foc_actions.map((ga) => <FocSubmitRow key={ga.id} gap={gap} gapAction={ga} />)
                )}
            </React.Fragment>
        ));
    }

    return (
        <>
            <button
                type="submit"
                className="btn btn-info"
                onClick={() => setShow(true)}
                disabled={!canSubmit || loading}
            >
                Submit
            </button>
            <BSModal
                className="attestation-modal-card foc-gap-modal"
                size="lg"
                show={show}
                onHide={() => setShow(false)}
                aria-labelledby="foc-submit-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="foc-submit-title" role="heading" aria-level="1">
                        Review AgeWise Submission
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!roleCanSubmit && (
                        <div className="permissions-alert">
                            You don’t have permission to create eSubmissions on this record.
                        </div>
                    )}
                    {submissionCategories.map(
                        (cat) =>
                            cat.gaps.length > 0 && (
                                <React.Fragment key={cat.name}>
                                    <div>
                                        <div className="h5 fw-bold">{cat.name}</div>
                                    </div>
                                    <table className="table table-sm table-striped w-100">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="col-gap-action-name">Measure</th>
                                                <th className="col-gap-action-name">
                                                    <span className="sr-only">Empty</span>
                                                    &nbsp;
                                                </th>
                                                <th className="w-20 text-center">Date</th>
                                                <th className="w-35 text-center">Results</th>
                                            </tr>
                                        </thead>
                                        <tbody>{displayTableBody(cat)}</tbody>
                                    </table>
                                </React.Fragment>
                            )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => setShow(false)}
                    >
                        {roleCanSubmit ? 'Cancel' : 'Close'}
                    </button>

                    {roleCanSubmit && (
                        <button
                            type="submit"
                            className="btn btn-primary"
                            onClick={submit}
                            disabled={loading}
                        >
                            {loading && <i className="fa-solid fa-spin fa-spinner me-2" />}
                            Submit
                        </button>
                    )}
                </Modal.Footer>
            </BSModal>
        </>
    );
}

FocSubmit.propTypes = propTypes;
